/**
 * Theme JavaScript
 * Handles theme functionality including cart, navigation, and interactions
 */

// Theme Configuration
const THEME = {
  name: 'Handcrafted Commerce',
  version: '1.0.0',
  settings: {
    cartType: 'drawer', // drawer or page
    enableQuantitySelectors: true,
    enableProductImageZoom: true,
    enableStickyHeader: false,
    enableScrollToTop: true
  }
};
const themeConfig = window.SHOPIFY_CONFIG || {};
const themeTranslations = themeConfig.translations || {};

// Utility Functions
const utils = {
  // Debounce function for performance optimization
  debounce: function(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = function() {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  },

  // Format money according to shop settings
  formatMoney: function(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    const value = (cents / 100).toFixed(2);
    
    if (format) {
      return format.replace('{{amount}}', value);
    }
    
    return `£${value}`;
  },

  // Show loading overlay
  showLoading: function() {
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
      overlay.classList.remove('hidden');
    }
  },

  // Hide loading overlay
  hideLoading: function() {
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
      overlay.classList.add('hidden');
    }
  },

  // Force hide loading overlay (emergency function)
  forceHideLoading: function() {
    const overlay = document.getElementById('loading-overlay');
    if (overlay) {
      overlay.classList.add('hidden');
      overlay.style.display = 'none';
    }
  },

  // Smooth scroll to element
  scrollToElement: function(element, offset = 0) {
    if (element) {
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  },

  // Get URL parameters
  getUrlParams: function() {
    const params = new URLSearchParams(window.location.search);
    const result = {};
    for (const [key, value] of params) {
      result[key] = value;
    }
    return result;
  },

  // Update URL without page reload
  updateUrl: function(key, value) {
    const url = new URL(window.location);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.pushState({}, '', url);
  }
};

// Runtime visual bindings (replaces inline style attributes)
const runtimeStyles = {
  applyHeaderLogoWidth: function(scope) {
    const root = scope || document;
    root.querySelectorAll('.header-logo[data-logo-width]').forEach((element) => {
      const rawValue = element.getAttribute('data-logo-width');
      const width = Number.parseInt(rawValue || '', 10);
      if (!Number.isNaN(width) && width > 0) {
        element.style.setProperty('--logo-width', `${width}px`);
      }
    });
  },

  applySwatchColors: function(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-swatch-color]').forEach((element) => {
      const color = element.getAttribute('data-swatch-color');
      if (color) {
        element.style.backgroundColor = color;
      }
    });
  },

  init: function(scope) {
    this.applyHeaderLogoWidth(scope);
    this.applySwatchColors(scope);
  }
};

// Unified Scroll Handler
// Single scroll listener that dispatches to all registered callbacks
// Uses requestAnimationFrame for optimal performance
const scrollHandler = {
  callbacks: {},
  lastScrollY: 0,
  ticking: false,
  initialized: false,
  
  // Register a scroll callback
  register: function(name, callback) {
    this.callbacks[name] = callback;
    
    // Initialize scroll listener on first registration
    if (!this.initialized) {
      this.init();
    }
  },
  
  // Unregister a scroll callback
  unregister: function(name) {
    delete this.callbacks[name];
  },
  
  // Initialize the scroll listener
  init: function() {
    if (this.initialized) return;
    
    this.lastScrollY = window.scrollY;
    
    const onScroll = () => {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          
          // Call all registered callbacks
          Object.values(this.callbacks).forEach(callback => {
            try {
              callback(scrollY, this.lastScrollY);
            } catch (e) {
              console.error('Scroll handler error:', e);
            }
          });
          
          this.lastScrollY = scrollY;
          this.ticking = false;
        });
        this.ticking = true;
      }
    };
    
    window.addEventListener('scroll', onScroll, { passive: true });
    this.initialized = true;
  }
};

// Cart Management
const cart = {
  getDrawerApi: function() {
    if (window.cartDrawer) return window.cartDrawer;
    return null;
  },

  // Add item to cart
  addItem: function(variantId, quantity = 1, properties = {}) {
    utils.showLoading();

    const drawer = this.getDrawerApi();
    if (drawer && typeof drawer.addToCart === 'function') {
      return drawer.addToCart(variantId, quantity, properties)
        .then(data => {
          this.updateCartCount();
          utils.hideLoading();
          return data;
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          utils.hideLoading();
          throw error;
        });
    }

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    Object.keys(properties).forEach(key => {
      formData.append(`properties[${key}]`, properties[key]);
    });

    return fetch('/cart/add.js', { method: 'POST', body: formData })
      .then(response => response.json())
      .then(data => {
        this.updateCartCount();
        utils.hideLoading();
        return data;
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        utils.hideLoading();
        throw error;
      });
  },

  // Update cart item quantity
  updateItem: function(line, quantity) {
    const formData = new FormData();
    formData.append('line', line);
    formData.append('quantity', quantity);

    utils.showLoading();

    return fetch('/cart/change.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      this.updateCartCount();
      this.refreshCartDrawer();
      utils.hideLoading();
      return data;
    })
    .catch(error => {
      console.error('Error updating cart:', error);
      utils.hideLoading();
      throw error;
    });
  },

  // Get cart data
  getCart: function() {
    return fetch('/cart.js').then(response => response.json());
  },

  // Update cart count in header
  updateCartCount: function() {
    this.getCart().then(cart => {
      const cartCountElements = document.querySelectorAll('.cart-count');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    }).catch(error => {
      console.error('Error updating cart count:', error);
    });
  },

  // Show cart drawer
  showCartDrawer: function() {
    const drawer = this.getDrawerApi();
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  },

  // Hide cart drawer
  hideCartDrawer: function() {
    const drawer = this.getDrawerApi();
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
  },

  // Refresh cart drawer content via single source of truth
  refreshCartDrawer: function() {
    const drawer = this.getDrawerApi();
    if (drawer && typeof drawer.refresh === 'function') {
      drawer.refresh();
      return;
    }

    if (drawer && typeof drawer.loadItems === 'function') {
      drawer.loadItems();
    }
  }
};

// Navigation
const navigation = {
  mobileMenuFocusTrapHandler: null,
  mobileMenuFocusReturnTarget: null,

  getFocusableElements: function(container) {
    if (!container) return [];
    const selector = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(selector)).filter((element) => {
      return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length) &&
        element.getAttribute('aria-hidden') !== 'true';
    });
  },

  activateMobileMenuFocusTrap: function(menuOverlay) {
    const panel = menuOverlay ? menuOverlay.querySelector('.mobile-menu-panel') : null;
    if (!menuOverlay || !panel) return;

    if (this.mobileMenuFocusTrapHandler) {
      document.removeEventListener('keydown', this.mobileMenuFocusTrapHandler, true);
      this.mobileMenuFocusTrapHandler = null;
    }

    if (!panel.hasAttribute('tabindex')) {
      panel.setAttribute('tabindex', '-1');
    }

    const focusable = this.getFocusableElements(panel);
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      panel.focus();
    }

    this.mobileMenuFocusTrapHandler = (e) => {
      if (!menuOverlay.classList.contains('active')) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeMobileMenu();
        return;
      }

      if (e.key !== 'Tab') return;

      const elements = this.getFocusableElements(panel);
      if (elements.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', this.mobileMenuFocusTrapHandler, true);
  },

  deactivateMobileMenuFocusTrap: function() {
    if (this.mobileMenuFocusTrapHandler) {
      document.removeEventListener('keydown', this.mobileMenuFocusTrapHandler, true);
      this.mobileMenuFocusTrapHandler = null;
    }
  },

  // Initialize mobile hamburger menu
  initMobileMenu: function() {
    const menuToggle = document.querySelector('.mobile-menu-toggle');
    const menuOverlay = document.getElementById('mobile-menu-overlay');
    const menuClose = document.querySelector('.mobile-menu-close');
    const menuBackdrop = document.querySelector('.mobile-menu-backdrop');
    
    if (menuToggle && menuOverlay) {
      // Open menu
      menuToggle.addEventListener('click', () => {
        this.openMobileMenu();
      });
      
      // Close menu via close button
      if (menuClose) {
        menuClose.addEventListener('click', () => {
          this.closeMobileMenu();
        });
      }
      
      // Close menu via backdrop click
      if (menuBackdrop) {
        menuBackdrop.addEventListener('click', () => {
          this.closeMobileMenu();
        });
      }
      
      // Close menu when screen size changes to desktop
      window.addEventListener('resize', utils.debounce(() => {
        if (window.innerWidth >= 640 && menuOverlay.classList.contains('active')) {
          this.closeMobileMenu();
        }
      }, 250));
    }
  },

  // Open mobile menu
  openMobileMenu: function() {
    const menuToggle = document.querySelector('.mobile-menu-toggle');
    const menuOverlay = document.getElementById('mobile-menu-overlay');
    
    if (menuToggle && menuOverlay) {
      if (menuOverlay.classList.contains('active')) return;

      this.mobileMenuFocusReturnTarget = document.activeElement instanceof HTMLElement ? document.activeElement : menuToggle;
      menuToggle.classList.add('active');
      menuToggle.setAttribute('aria-expanded', 'true');
      menuOverlay.classList.remove('hidden');
      menuOverlay.classList.add('active');
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      this.activateMobileMenuFocusTrap(menuOverlay);
    }
  },

  // Close mobile menu
  closeMobileMenu: function() {
    const menuToggle = document.querySelector('.mobile-menu-toggle');
    const menuOverlay = document.getElementById('mobile-menu-overlay');
    
    if (menuToggle && menuOverlay) {
      menuToggle.classList.remove('active');
      menuToggle.setAttribute('aria-expanded', 'false');
      menuOverlay.classList.remove('active');
      
      // Restore body scroll
      document.body.style.overflow = '';

      this.deactivateMobileMenuFocusTrap();

      if (
        this.mobileMenuFocusReturnTarget &&
        typeof this.mobileMenuFocusReturnTarget.focus === 'function' &&
        this.mobileMenuFocusReturnTarget.isConnected
      ) {
        this.mobileMenuFocusReturnTarget.focus({ preventScroll: true });
      } else {
        menuToggle.focus({ preventScroll: true });
      }
      this.mobileMenuFocusReturnTarget = null;
      
      // Hide after animation
      setTimeout(() => {
        if (!menuOverlay.classList.contains('active')) {
          menuOverlay.classList.add('hidden');
        }
      }, 300);
    }
  },

  // Initialize navigation dropdowns
  initNavigationDropdowns: function() {
    // Desktop navigation dropdown functionality (click to toggle)
    const navDropdownToggles = document.querySelectorAll('.header-nav-dropdown-toggle');
    
    navDropdownToggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
        const arrow = toggle.querySelector('.nav-dropdown-arrow');
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        
        // Close all other dropdowns
        navDropdownToggles.forEach((otherToggle) => {
          if (otherToggle !== toggle) {
            const otherSubmenu = document.getElementById(otherToggle.getAttribute('aria-controls'));
            const otherArrow = otherToggle.querySelector('.nav-dropdown-arrow');
            otherToggle.setAttribute('aria-expanded', 'false');
            if (otherSubmenu) otherSubmenu.classList.add('hidden');
            if (otherArrow) otherArrow.classList.remove('rotated');
          }
        });
        
        // Toggle current dropdown
        toggle.setAttribute('aria-expanded', !isExpanded);
        if (submenu) submenu.classList.toggle('hidden');
        if (arrow) arrow.classList.toggle('rotated');
      });
    });
    
    // Mobile navigation dropdown functionality
    const mobileDropdownToggles = document.querySelectorAll('.mobile-nav-dropdown-toggle');
    
    mobileDropdownToggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
        const arrow = toggle.querySelector('.mobile-dropdown-arrow');
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        
        // Toggle dropdown
        toggle.setAttribute('aria-expanded', !isExpanded);
        if (submenu) submenu.classList.toggle('hidden');
        if (arrow) arrow.classList.toggle('rotated');
      });
    });
    
    // Close all navigation dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      const isNavClick = e.target.closest('.nav-dropdown') || e.target.closest('.mobile-nav-dropdown');
      
      if (!isNavClick) {
        // Close desktop dropdowns
        navDropdownToggles.forEach((toggle) => {
          const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
          const arrow = toggle.querySelector('.nav-dropdown-arrow');
          toggle.setAttribute('aria-expanded', 'false');
          if (submenu) submenu.classList.add('hidden');
          if (arrow) arrow.classList.remove('rotated');
        });
        
        // Close mobile dropdowns
        mobileDropdownToggles.forEach((toggle) => {
          const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
          const arrow = toggle.querySelector('.mobile-dropdown-arrow');
          toggle.setAttribute('aria-expanded', 'false');
          if (submenu) submenu.classList.add('hidden');
          if (arrow) arrow.classList.remove('rotated');
        });
      }
    });
  },

  // Initialize dropdown menus
  initDropdowns: function() {
    const dropdowns = document.querySelectorAll('[data-dropdown]');
    
    dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('[data-dropdown-trigger]');
      const menu = dropdown.querySelector('[data-dropdown-menu]');
      
      if (trigger && menu) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Close other dropdowns
          dropdowns.forEach(other => {
            if (other !== dropdown) {
              other.querySelector('[data-dropdown-menu]').classList.add('hidden');
              other.querySelector('[data-dropdown-trigger]').setAttribute('aria-expanded', 'false');
            }
          });
          
          // Toggle current dropdown
          const isOpen = !menu.classList.contains('hidden');
          menu.classList.toggle('hidden');
          trigger.setAttribute('aria-expanded', !isOpen);
        });
      }
    });
    
    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      dropdowns.forEach(dropdown => {
        const menu = dropdown.querySelector('[data-dropdown-menu]');
        const trigger = dropdown.querySelector('[data-dropdown-trigger]');
        if (menu && trigger) {
          menu.classList.add('hidden');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  },

  // Initialize sticky header (registers with unified scroll handler)
  initStickyHeader: function() {
    if (!THEME.settings.enableStickyHeader) return;
    
    const header = document.querySelector('.header');
    if (!header) return;
    
    // Register with unified scroll handler
    scrollHandler.register('stickyHeader', (scrollY, lastScrollY) => {
      if (scrollY > 100) {
        if (scrollY > lastScrollY) {
          // Scrolling down
          header.classList.add('header-hidden');
        } else {
          // Scrolling up
          header.classList.remove('header-hidden');
        }
      } else {
        header.classList.remove('header-hidden');
      }
    });
  }
};

// Product functionality
const product = {
  // Initialize product image gallery
  initImageGallery: function() {
    const thumbnails = document.querySelectorAll('.thumbnail-image');
    const mainImage = document.getElementById('main-product-image');
    
    if (thumbnails.length > 0 && mainImage) {
      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', () => {
          const newSrc = thumbnail.dataset.fullSize || thumbnail.src;
          mainImage.src = newSrc;
          
          // Update active thumbnail
          thumbnails.forEach(t => t.classList.remove('active'));
          thumbnail.classList.add('active');
        });
      });
    }
  },

  // Initialize quantity selectors
  initQuantitySelectors: function() {
    if (!THEME.settings.enableQuantitySelectors) return;
    
    const quantitySelectors = document.querySelectorAll('.quantity-selector');
    
    quantitySelectors.forEach(selector => {
      const input = selector.querySelector('input[type="number"]');
      const minusBtn = selector.querySelector('.quantity-minus');
      const plusBtn = selector.querySelector('.quantity-plus');
      
      if (input && minusBtn && plusBtn) {
        minusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value);
          if (currentValue > 1) {
            input.value = currentValue - 1;
            input.dispatchEvent(new Event('change'));
          }
        });
        
        plusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value);
          input.value = currentValue + 1;
          input.dispatchEvent(new Event('change'));
        });
      }
    });
  },

  // Initialize product forms
  initProductForms: function() {
    const productForms = document.querySelectorAll('[data-product-form]');
    
    productForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        
        // Get properties
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propertyName = key.replace('properties[', '').replace(']', '');
            properties[propertyName] = value;
          }
        }
        
        cart.addItem(variantId, quantity, properties)
          .then(() => {
            // Show success message
            this.showAddToCartSuccess();
          })
          .catch(error => {
            // Show error message
            this.showAddToCartError(error);
          });
      });
    });
  },

  // Show add to cart success message
  showAddToCartSuccess: function() {
    // Create and show success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        <span>Product added to cart!</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.remove();
    }, 3000);
  },

  // Show add to cart error message
  showAddToCartError: function(error) {
    // Create and show error notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50';
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
        </svg>
        <span>Error adding product to cart</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove notification after 3 seconds
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
};

// Search functionality
const search = {
  // Initialize search
  initSearch: function() {
    const searchForms = document.querySelectorAll('[data-search-form]');
    const searchInputs = document.querySelectorAll('[data-search-input]');
    
    searchInputs.forEach(input => {
      // Add search suggestions (if enabled)
      this.initSearchSuggestions(input);
    });
  },

  // Initialize search suggestions
  initSearchSuggestions: function(input) {
    let timeout;
    
    input.addEventListener('input', utils.debounce((e) => {
      const query = e.target.value.trim();
      
      if (query.length >= 2) {
        this.fetchSearchSuggestions(query);
      } else {
        this.hideSuggestions();
      }
    }, 300));
    
    // Hide suggestions when input loses focus
    input.addEventListener('blur', () => {
      setTimeout(() => {
        this.hideSuggestions();
      }, 200);
    });
  },

  // Fetch search suggestions
  fetchSearchSuggestions: function(query) {
    fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`)
      .then(response => response.json())
      .then(data => {
        this.showSuggestions(data);
      })
      .catch(error => {
        console.error('Error fetching search suggestions:', error);
      });
  },

  // Show search suggestions
  showSuggestions: function(suggestions) {
    // Implementation depends on your search suggestions UI
    void suggestions;
  },

  // Hide search suggestions
  hideSuggestions: function() {
    const suggestionsContainer = document.querySelector('.search-suggestions');
    if (suggestionsContainer) {
      suggestionsContainer.classList.add('hidden');
    }
  }
};

// Read More functionality
const readMore = {
  init: function() {
    const btn = document.getElementById('read-more-btn');
    const textContainer = document.getElementById('intro-text');

    if (btn && textContainer) {
      btn.addEventListener('click', function() {
        // Toggle the class
        textContainer.classList.add('expanded');
        // Update accessibility attribute
        btn.setAttribute('aria-expanded', 'true');
        // Hide the button after clicking
        btn.style.display = 'none';
      });
    }
  }
};

// Customer login page interactions
const customerAuth = {
  init: function() {
    document.addEventListener('click', function(e) {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');
      if (action === 'show-recover-password') {
        e.preventDefault();
        const recoverForm = document.getElementById('recover-password-form');
        const recoverEmail = document.getElementById('recover-email');
        if (recoverForm) recoverForm.classList.remove('hidden');
        if (recoverEmail) recoverEmail.focus();
      }

      if (action === 'hide-recover-password') {
        e.preventDefault();
        const recoverForm = document.getElementById('recover-password-form');
        if (recoverForm) recoverForm.classList.add('hidden');
      }
    });
  }
};

// Scroll to top functionality
const scrollToTop = {
  init: function() {
    if (!THEME.settings.enableScrollToTop) return;
    
    // Create scroll to top button
    const button = document.createElement('button');
    button.className = 'fixed bottom-8 right-8 bg-forest text-white p-3 rounded-full shadow-lg hover:bg-walnut transition-colors z-40 hidden';
    button.innerHTML = `
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
      </svg>
    `;
    button.setAttribute('aria-label', themeTranslations.scrollToTopLabel || 'Scroll to top');
    
    document.body.appendChild(button);
    
    // Register with unified scroll handler
    scrollHandler.register('scrollToTop', (scrollY) => {
      if (scrollY > 400) {
        button.classList.remove('hidden');
      } else {
        button.classList.add('hidden');
      }
    });
    
    // Scroll to top when clicked
    button.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }
};

// Initialize theme
const init = function() {
  // Emergency loading overlay fix - hide immediately
  utils.forceHideLoading();
  
  // Initialize all components
  navigation.initMobileMenu();
  navigation.initNavigationDropdowns();
  navigation.initDropdowns();
  navigation.initStickyHeader();
  runtimeStyles.init();
  
  product.initImageGallery();
  product.initQuantitySelectors();
  product.initProductForms();
  
  search.initSearch();
  scrollToTop.init();
  readMore.init();
  customerAuth.init();
  
  // Add loaded class to body
  document.body.classList.add('theme-loaded');
  
  // Additional loading overlay safety measures
  document.addEventListener('DOMContentLoaded', function() {
    utils.forceHideLoading();
    
    // Safety timeout to hide loading overlay after 5 seconds
    setTimeout(function() {
      utils.forceHideLoading();
    }, 5000);
  });
  
  // Allow clicking anywhere on the overlay to dismiss loading overlay
  document.addEventListener('click', function(e) {
    const overlay = document.getElementById('loading-overlay');
    if (overlay && !overlay.classList.contains('hidden') && overlay.contains(e.target)) {
      utils.forceHideLoading();
    }
  });

  // Emergency escape key to hide loading overlay
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      utils.forceHideLoading();
    }
  });
  
  // Theme initialization intentionally silent in production.
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export minimal public API used by other theme modules.
window.cart = cart;
